import React, { useState } from 'react';
import { Box, Button, Dialog, DialogTitle, DialogContent, DialogActions, Typography, Alert, LinearProgress, Table, TableBody, TableCell, TableHead, TableRow, Stack, Chip } from '@mui/material';
import api from '../api/axios';

const CHUNK_SIZE = 100;
const FIELDS = ['email', 'first_name', 'last_name', 'phone_number', 'username', 'password', 'password_hash', 'referral_id', 'affiliate_id'] as const;
type Field = typeof FIELDS[number];
type ImportRow = Partial<Record<Field, string>>;

const HEADER_ALIASES: Record<string, Field> = {
  email: 'email', email_address: 'email',
  first_name: 'first_name', firstname: 'first_name', first: 'first_name',
  last_name: 'last_name', lastname: 'last_name', last: 'last_name',
  phone_number: 'phone_number', phone: 'phone_number', phonenumber: 'phone_number', mobile: 'phone_number',
  username: 'username', user_name: 'username',
  password: 'password',
  password_hash: 'password_hash', hashed_password: 'password_hash', hash: 'password_hash',
  referral_id: 'referral_id', affiliate_id: 'affiliate_id',
};

const TEMPLATE = 'email,first_name,last_name,phone_number,username,password,password_hash\n';
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface RowResult {
  row: number;
  email: string | null;
  status: 'created' | 'skipped' | 'failed';
  reason?: string;
  needs_reset?: boolean;
}

// RFC 4180 CSV: quoted fields, escaped quotes, commas/newlines inside quotes
function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"' && text[i + 1] === '"') { field += '"'; i++; }
      else if (c === '"') inQuotes = false;
      else field += c;
    } else if (c === '"') inQuotes = true;
    else if (c === ',') { row.push(field); field = ''; }
    else if (c === '\n' || c === '\r') {
      if (c === '\r' && text[i + 1] === '\n') i++;
      row.push(field); field = '';
      rows.push(row); row = [];
    } else field += c;
  }
  if (field !== '' || row.length) { row.push(field); rows.push(row); }
  return rows.filter(r => r.some(v => v.trim() !== ''));
}

function toCsv(rows: (string | number | boolean | null | undefined)[][]): string {
  return rows.map(r => r.map(v => {
    const s = v === null || v === undefined ? '' : String(v);
    return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  }).join(',')).join('\n');
}

function download(name: string, content: string) {
  const url = URL.createObjectURL(new Blob([content], { type: 'text/csv' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}

function errorMessage(err: any, fallback: string): string {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail) && detail[0]?.msg) return detail[0].msg;
  return fallback;
}

interface UserImportDialogProps {
  open: boolean;
  tenantId?: number;
  onClose: () => void;
  onImported: () => void;
}

const UserImportDialog: React.FC<UserImportDialogProps> = ({ open, tenantId, onClose, onImported }) => {
  const [fileName, setFileName] = useState('');
  const [rows, setRows] = useState<ImportRow[]>([]);
  const [columns, setColumns] = useState<Field[]>([]);
  const [ignored, setIgnored] = useState<string[]>([]);
  const [parseError, setParseError] = useState('');
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState<RowResult[] | null>(null);
  const [lastRunDry, setLastRunDry] = useState(false);
  const [runError, setRunError] = useState('');

  const reset = () => {
    setFileName(''); setRows([]); setColumns([]); setIgnored([]); setParseError('');
    setProgress(0); setResults(null); setRunError('');
  };

  const handleClose = () => {
    if (running) return;
    reset();
    onClose();
  };

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    reset();
    setFileName(file.name);
    const table = parseCsv((await file.text()).replace(/^﻿/, ''));
    if (table.length < 2) {
      setParseError('CSV needs a header row and at least one user.');
      return;
    }
    const header = table[0].map(h => h.trim().toLowerCase().replace(/[\s-]+/g, '_'));
    const mapped = header.map(h => HEADER_ALIASES[h]);
    if (!mapped.includes('email')) {
      setParseError('CSV must have an "email" column.');
      return;
    }
    setColumns(Array.from(new Set(mapped.filter((f): f is Field => !!f))));
    setIgnored(table[0].filter((_, i) => !mapped[i]));
    setRows(table.slice(1).map(cells => {
      const row: ImportRow = {};
      mapped.forEach((f, i) => {
        const v = (cells[i] ?? '').trim();
        if (f && v) row[f] = f === 'password' ? cells[i] : v;
      });
      return row;
    }));
  };

  const emails = rows.map(r => (r.email || '').trim());
  const invalidEmails = emails.filter(e => !EMAIL_RE.test(e)).length;
  const duplicateEmails = emails.length - new Set(emails.filter(Boolean)).size - emails.filter(e => !e).length;

  const run = async (dryRun: boolean) => {
    setRunning(true);
    setRunError('');
    setResults(null);
    setProgress(0);
    setLastRunDry(dryRun);
    const token = localStorage.getItem('token');
    const all: RowResult[] = [];
    try {
      for (let start = 0; start < rows.length; start += CHUNK_SIZE) {
        const chunk = rows.slice(start, start + CHUNK_SIZE);
        const res = await api.post(
          '/api/v1/admin/user-management/users/import',
          { tenant_id: tenantId, dry_run: dryRun, rows: chunk },
          { headers: { Authorization: `Bearer ${token}` }, timeout: 120000 },
        );
        const data = res.data as { results: RowResult[] };
        // Backend row index is per chunk; convert to the CSV line number (header is line 1)
        all.push(...data.results.map(r => ({ ...r, row: start + r.row + 2 })));
        setProgress(Math.min(start + CHUNK_SIZE, rows.length));
        setResults([...all]);
      }
    } catch (err: any) {
      const done = all.length;
      setRunError(`${errorMessage(err, 'Import request failed')} — stopped after ${done} of ${rows.length} rows.${dryRun ? '' : ' Rows already processed were saved; re-running the file skips them.'}`);
    } finally {
      setRunning(false);
      if (!dryRun && all.some(r => r.status === 'created')) onImported();
    }
  };

  const count = (status: RowResult['status']) => (results || []).filter(r => r.status === status).length;
  const needsReset = (results || []).filter(r => r.status === 'created' && r.needs_reset).length;
  const problems = (results || []).filter(r => r.status !== 'created');

  const downloadReport = () => {
    download(`user-import-report${lastRunDry ? '-dry-run' : ''}.csv`, toCsv([
      ['csv_line', 'email', 'status', 'reason', 'needs_password_reset'],
      ...(results || []).map(r => [r.row, r.email, r.status, r.reason, r.status === 'created' ? !!r.needs_reset : '']),
    ]));
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Import Users from CSV</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Required column: <b>email</b>. Optional: first_name, last_name, phone_number, username, password, password_hash.
          A bcrypt <b>password_hash</b> keeps the player's old password. A plain <b>password</b> must be at least 8 characters.
          Rows with neither get a random password and the player must use Forgot Password.
          Emails already registered on this site are skipped.
        </Typography>
        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
          <Button variant="outlined" component="label" disabled={running}>
            Choose CSV
            <input type="file" accept=".csv,text/csv" hidden onChange={handleFile} />
          </Button>
          <Button onClick={() => download('user-import-template.csv', TEMPLATE)} disabled={running}>Download template</Button>
          {fileName && <Typography sx={{ alignSelf: 'center' }}>{fileName}</Typography>}
        </Stack>

        {parseError && <Alert severity="error" sx={{ mb: 2 }}>{parseError}</Alert>}

        {rows.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography><b>{rows.length}</b> users found. Columns: {columns.join(', ')}</Typography>
            {ignored.length > 0 && <Typography variant="body2" color="text.secondary">Ignored columns: {ignored.join(', ')}</Typography>}
            {invalidEmails > 0 && <Alert severity="warning" sx={{ mt: 1 }}>{invalidEmails} rows have a missing or invalid email and will fail.</Alert>}
            {duplicateEmails > 0 && <Alert severity="warning" sx={{ mt: 1 }}>{duplicateEmails} rows repeat an email already in the file and will be skipped.</Alert>}
            {!results && (
              <Table size="small" sx={{ mt: 1 }}>
                <TableHead>
                  <TableRow>{columns.filter(c => c !== 'password' && c !== 'password_hash').map(c => <TableCell key={c}>{c}</TableCell>)}</TableRow>
                </TableHead>
                <TableBody>
                  {rows.slice(0, 10).map((r, i) => (
                    <TableRow key={i}>{columns.filter(c => c !== 'password' && c !== 'password_hash').map(c => <TableCell key={c}>{r[c]}</TableCell>)}</TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Box>
        )}

        {(running || results) && (
          <Box sx={{ mb: 2 }}>
            <LinearProgress variant="determinate" value={rows.length ? (progress / rows.length) * 100 : 0} sx={{ mb: 1 }} />
            <Typography variant="body2">{lastRunDry ? 'Validated' : 'Processed'} {progress} / {rows.length}</Typography>
          </Box>
        )}

        {runError && <Alert severity="error" sx={{ mb: 2 }}>{runError}</Alert>}

        {results && !running && (
          <Box>
            <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
              <Chip color="success" label={`${lastRunDry ? 'Would create' : 'Created'}: ${count('created')}`} />
              <Chip color="warning" label={`Skipped: ${count('skipped')}`} />
              <Chip color="error" label={`Failed: ${count('failed')}`} />
              {needsReset > 0 && <Chip label={`Need password reset: ${needsReset}`} />}
              <Button size="small" onClick={downloadReport}>Download report</Button>
            </Stack>
            {problems.length > 0 && (
              <Table size="small">
                <TableHead>
                  <TableRow><TableCell>CSV line</TableCell><TableCell>Email</TableCell><TableCell>Status</TableCell><TableCell>Reason</TableCell></TableRow>
                </TableHead>
                <TableBody>
                  {problems.slice(0, 100).map(r => (
                    <TableRow key={r.row}><TableCell>{r.row}</TableCell><TableCell>{r.email}</TableCell><TableCell>{r.status}</TableCell><TableCell>{r.reason}</TableCell></TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            {problems.length > 100 && <Typography variant="body2" color="text.secondary">Showing first 100 — download the report for all.</Typography>}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={running}>Close</Button>
        <Button onClick={() => run(true)} disabled={running || rows.length === 0}>Validate (dry run)</Button>
        <Button variant="contained" onClick={() => run(false)} disabled={running || rows.length === 0}>Import {rows.length || ''} users</Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserImportDialog;
